/**
 * DOM/Glaze-free pure helpers for `CreatePlaySidebar` (`components/CreatePlaySidebar.tsx`).
 * Deliberately import-free of Glaze/React so bun's DOM-less test runner can
 * exercise this logic directly — same pattern as `config-page-logic.ts`.
 *
 * QA round 7 (item 10) — the sidebar no longer names the play at create time
 * (two "Name" fields read as a duplicate); a play is named later via Rename
 * on its row or in its header (`usePlayRowActions`).
 */

import type { PlaybooksPlaybookSummary } from './schemas';

export type CreatePlayMode = 'existing' | 'new';

export interface CreatePlayForm {
  mode: CreatePlayMode;
  /** Chosen library playbook id (existing mode). */
  selectedPlaybookId: string | null;
  /** New-playbook fields (new mode). */
  newName: string;
  newDescription: string;
}

export const initialCreatePlayForm: CreatePlayForm = {
  mode: 'existing',
  selectedPlaybookId: null,
  newName: '',
  newDescription: '',
};

/** `"${name} · N rungs"` (singular "1 rung"); `rungCount` comes from the library summary. */
export function playbookOptionLabel(pb: PlaybooksPlaybookSummary): string {
  return `${pb.name} · ${pb.rungCount} ${pb.rungCount === 1 ? 'rung' : 'rungs'}`;
}

/**
 * Case-insensitive substring match on `name` (and `description` when
 * non-null); a blank/whitespace query returns the input array unchanged.
 */
export function filterPlaybooksByQuery(
  playbooks: readonly PlaybooksPlaybookSummary[],
  query: string,
): PlaybooksPlaybookSummary[] {
  const trimmed = query.trim().toLowerCase();
  if (trimmed === '') return [...playbooks];
  return playbooks.filter(
    (pb) =>
      pb.name.toLowerCase().includes(trimmed) ||
      (pb.description !== null && pb.description.toLowerCase().includes(trimmed)),
  );
}

/** existing → needs `selectedPlaybookId`; new → needs a non-blank `newName`; always false while submitting. */
export function canConfirmCreatePlay(form: CreatePlayForm, submitting: boolean): boolean {
  if (submitting) return false;
  if (form.mode === 'existing') return form.selectedPlaybookId !== null;
  return form.newName.trim() !== '';
}

/**
 * After the inline playbook create succeeds but BEFORE `createPlay` runs,
 * lock the form onto the just-created playbook: mode `'existing'` + selected
 * id. A retry after a `createPlay` failure then never creates a duplicate
 * playbook.
 */
export function formAfterPlaybookCreated(form: CreatePlayForm, createdPlaybookId: string): CreatePlayForm {
  return {
    ...form,
    mode: 'existing',
    selectedPlaybookId: createdPlaybookId,
  };
}

/** `description.trim() === '' ? null : description.trim()` — what to send on the wire. */
export function normalizeDescription(description: string): string | null {
  const trimmed = description.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * Human copy for the sidebar's inline error. Detects the `FetchErrorLike`
 * shape the same way `GenerateWithAIDrawer.tsx`'s `isVersionConflictError`
 * does — `typeof err === 'object' && err !== null && 'code' in err`.
 */
export function createPlayErrorMessage(err: unknown): string {
  if (typeof err === 'object' && err !== null && 'code' in err && (err as { code?: unknown }).code === 'identity_archived') {
    return 'That Playbook is archived — pick a different one.';
  }
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return 'Could not create a play for this campaign — try again.';
}
